import React from 'react';
import {
  TripMatch,
  TripRequest,
  ConversationSummaries,
  ConversationType,
} from '@marageti/z4-sdk/lib/travel';
import { SxProps, Theme } from '@mui/material';
import { OutlinedIcons, useApiClient, useFetch } from '@marageti/z4-lib';
import ConversationLinksCard, { ConversationLinksCardProps } from './ConversationLinksCard';
import { ConversationLinkProps } from './ConversationLink';
import { getConversationSummaryByConversationId, getConversationSummaryByConversationType } from './helpers';
import { ConversationLinkMap } from './types';

type TripRequestConversationLinksProps = {
  tripRequestId: string;
  renderConversationLink: ConversationLinksCardProps['renderConversationLink'];
  getConversationTypeTitle: ConversationLinkProps['getConversationTypeTitle'];
  tripMatchId?: string;
};

const TripRequestConversationLinks = ({
  tripRequestId,
  renderConversationLink,
  getConversationTypeTitle,
  tripMatchId,
}: TripRequestConversationLinksProps) => {
  const apiClient = useApiClient();

  const {
    data: tripMatch,
    error: tripMatchError,
    isLoading: isTripMatchLoading,
  } = useFetch<TripMatch>(
    apiClient.tripMatchesClient.getTripMatch,
    [tripMatchId],
    !!tripMatchId,
  );
  // Fetch all required data upfront
  const {
    data: tripRequest,
    error: tripRequestError,
    isLoading: isTripRequestLoading,
  } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [tripRequestId],
    !!tripRequestId,
  );

  const {
    data: allConversations,
    error: allConversationsError,
    isLoading: isAllConversationsLoading,
  } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripRequestId],
    !!tripRequestId,
  );

  const {
    data: assignedTeamMemberConversations,
    error: assignedTeamMemberError,
    isLoading: isAssignedTeamMemberLoading,
  } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripRequestId, { params: { person: tripRequest?.assignedTo } }],
    !!(tripRequestId && tripRequest?.assignedTo),
  );

  const agentId = tripMatch?.assignedTo;

  const {
    data: allAgentSummaries,
    error: agentSummariesError,
    isLoading: isAgentSummariesLoading,
  } = useFetch<ConversationSummaries[]>(
    async () => {
      if (!agentId) return [];

      const summariesPromises = agentId ? [apiClient.tripRequestsClient.getConversationSummaries(
        tripRequestId,
        { params: { person: agentId } },
      )] : [];

      return Promise.all(summariesPromises);
    },
    [tripRequestId, JSON.stringify(agentId)],
    !!tripRequestId && !!agentId,
  );

  // Handle loading state
  const isLoading = isTripRequestLoading
    || isTripMatchLoading
    || isAllConversationsLoading
    || isAssignedTeamMemberLoading
    || isAgentSummariesLoading;

  if (isLoading) {
    return <TripRequestConversationLinks.Skeleton sx={{ padding: 0 }} />;
  }

  // Handle error state
  const hasError = tripRequestError
    || tripMatchError
    || allConversationsError
    || assignedTeamMemberError
    || agentSummariesError
    || !allConversations
    || !tripRequest
    || !tripMatch;

  if (hasError) {
    return <div>Error loading conversation data</div>;
  }

  // Get default conversation summary
  const defaultTravelerToZicasso = getConversationSummaryByConversationType(
    ConversationType.TravelerToZicasso,
    allConversations,
  );

  if (!defaultTravelerToZicasso) {
    return <div>Error loading conversation data</div>;
  }

  const defaultTravelerToPartner = getConversationSummaryByConversationId(
    tripMatch.travelerToPartnerId,
    allConversations,
  );
  const defaultZicassoToPartner = getConversationSummaryByConversationId(
    tripMatch.zicassoToPartnerId,
    allConversations,
  );

  if (!defaultTravelerToPartner || !defaultZicassoToPartner) {
    return null;
  }

  const summaries: ConversationLinkMap = {
    [ConversationType.TravelerToPartner]: {
      ...(tripMatch.assignedTo && allAgentSummaries && {
        agentContext: getConversationSummaryByConversationId(
          tripMatch.travelerToPartnerId,
          allAgentSummaries.flat(),
        ),
      }),
      defaultContext: defaultTravelerToPartner,
    },
    [ConversationType.ZicassoToPartner]: {
      ...(tripMatch.assignedTo && allAgentSummaries && {
        agentContext: getConversationSummaryByConversationId(
          tripMatch.zicassoToPartnerId,
          allAgentSummaries.flat(),
        ),
      }),
      ...(tripRequest.assignedTo && assignedTeamMemberConversations && {
        teamMemberContext: getConversationSummaryByConversationId(
          tripMatch.zicassoToPartnerId,
          assignedTeamMemberConversations,
        ),
      }),
      defaultContext: defaultZicassoToPartner,
    },
  };

  const getConversationIcon = () => <OutlinedIcons.OutlinedChatIcon />;

  return (
    <ConversationLinksCard
      key={tripMatch.id}
      conversationSummaries={summaries}
      tripMatchId={tripMatch.id}
      tripRequestId={tripRequestId}
      renderConversationLink={renderConversationLink}
      getConversationTypeTitle={getConversationTypeTitle}
      getConversationIcon={getConversationIcon}
      sx={{
        padding: 0,
        backgroundColor: 'primary.contrastText',
      }}
    />
  );
};

TripRequestConversationLinks.Skeleton = ({ sx }: { sx?: SxProps<Theme> }) => (
  <ConversationLinksCard.Skeleton sx={sx} />
);

export default TripRequestConversationLinks;
